/**
 * ROAR-5007 (P1.14, AC11) — the company's per-feature plan access, as `validateLogin` returns it on
 * `user.features` (ROAR-4977): `{ [featureKey]: { status: 'granted' | 'locked', hide: boolean } }`.
 *
 * The UI lock is a convenience only — the API still rejects calls to a locked feature (ROAR-4979).
 */

/** `billing.features` catalog keys the portal gates on. */
export const FEATURE_KEYS = Object.freeze({
  INTEGRATION_SLACK: 'integration_slack',
  INTEGRATION_GITHUB: 'integration_github',
  INTEGRATION_JIRA: 'integration_jira',
  INTEGRATION_SERVICENOW: 'integration_servicenow',
  INTEGRATION_OKTA: 'integration_okta',
  INTEGRATION_QUALYS: 'integration_qualys',
  INTEGRATION_CICD_SECURE_GATE: 'integration_cicd_secure_gate',
  CTEM_DASHBOARD: 'ctem_dashboard',
  EASM: 'easm',
  GENPT_WEB: 'genpt_web',
  GENPT_API: 'genpt_api',
  GENPT_API_IDOR: 'genpt_api_idor',
  GENPT_BUSINESS_SCENARIO: 'genpt_business_scenario',
  GENPT_SCHEDULING: 'genpt_scheduling',
  GENVA: 'genva',
  PENTEST: 'pentest',
  BILLING: 'billing',
  USER_INVITE: 'user_invite',
});

export const FEATURE_ACCESS_STATUS = Object.freeze({
  GRANTED: 'granted',
  LOCKED: 'locked',
});

/** The name the upgrade request names the feature by ("Upgrade request — <Feature> — <Company>"). */
export const FEATURE_LABELS = Object.freeze({
  [FEATURE_KEYS.INTEGRATION_SLACK]: 'Slack',
  [FEATURE_KEYS.INTEGRATION_GITHUB]: 'GitHub',
  [FEATURE_KEYS.INTEGRATION_JIRA]: 'Jira',
  [FEATURE_KEYS.INTEGRATION_SERVICENOW]: 'ServiceNow',
  [FEATURE_KEYS.INTEGRATION_OKTA]: 'Okta',
  [FEATURE_KEYS.INTEGRATION_QUALYS]: 'Qualys',
  [FEATURE_KEYS.INTEGRATION_CICD_SECURE_GATE]: 'CI/CD Secure Gate',
  [FEATURE_KEYS.EASM]: 'EASM',
  [FEATURE_KEYS.GENVA]: 'GenVA',
});

/**
 * The access status of one feature, or null when the map does not mention it.
 *
 * Accepts the earlier flat shape (`{ easm: 'granted' }`) as well as `{ status, hide }`: the map's
 * shape changed during ROAR-4977 and a stale cached `validateLogin` can still carry the old one.
 *
 * @param {object|null|undefined} features `user.features`.
 * @param {string} featureKey
 * @returns {string|null}
 */
export const featureStatus = (features, featureKey) => {
  const entry = features?.[featureKey];
  if (entry == null) return null;
  return typeof entry === 'string' ? entry : entry.status ?? null;
};

/**
 * True only for an explicit `locked`. A missing map or a key the backend does not send is NOT locked:
 * an older backend, or a feature added to the catalog before the portal knows it, must not lock the
 * customer out of something their plan includes. The API remains the real gate.
 *
 * @param {object|null|undefined} features `user.features`.
 * @param {string} featureKey
 * @returns {boolean}
 */
export const isFeatureLocked = (features, featureKey) =>
  featureStatus(features, featureKey) === FEATURE_ACCESS_STATUS.LOCKED;

/**
 * True only for an explicit `hide: true`. Like `isFeatureLocked`, a missing map, key or flag shows
 * the feature — and the earlier flat string shape carries no `hide` at all.
 *
 * @param {object|null|undefined} features `user.features`.
 * @param {string} featureKey
 * @returns {boolean}
 */
export const isFeatureHidden = (features, featureKey) => {
  const entry = features?.[featureKey];
  return !!entry && typeof entry === 'object' && entry.hide === true;
};

/**
 * Top-level sidebar items (by menu `label`) and the features behind them. An item is locked only
 * when EVERY one of its features is locked — GenPT stays reachable while any of its test types or
 * scheduling is in the plan.
 */
export const SIDEBAR_FEATURE_KEYS = Object.freeze({
  'Mission Control': [FEATURE_KEYS.CTEM_DASHBOARD],
  EASM: [FEATURE_KEYS.EASM],
  GenPT: [
    FEATURE_KEYS.GENPT_WEB,
    FEATURE_KEYS.GENPT_API,
    FEATURE_KEYS.GENPT_API_IDOR,
    FEATURE_KEYS.GENPT_SCHEDULING,
  ],
  GenVA: [FEATURE_KEYS.GENVA],
  Pentests: [FEATURE_KEYS.PENTEST],
});

/**
 * Whether a top-level sidebar item is locked by the company's plan. Items not in
 * `SIDEBAR_FEATURE_KEYS` are never locked.
 *
 * @param {object|null|undefined} features `user.features`.
 * @param {string} label The menu item's `label`.
 * @returns {boolean}
 */
export const isSidebarItemLocked = (features, label) => {
  const keys = SIDEBAR_FEATURE_KEYS[label];
  return !!keys && keys.every((key) => isFeatureLocked(features, key));
};

/**
 * Sidebar child items (by menu `label`) gated by a single feature. Unlike a top-level item, a child
 * is one screen behind one feature, so it locks as soon as that feature is locked — e.g. GenPT stays
 * open on a plan without scheduling, but its "All Schedules" entry does not.
 */
export const SIDEBAR_CHILD_FEATURE_KEYS = Object.freeze({
  'All Schedules': [FEATURE_KEYS.GENPT_SCHEDULING],
});

/**
 * Whether a sidebar child item is locked by the company's plan. Items not in
 * `SIDEBAR_CHILD_FEATURE_KEYS` are never locked.
 *
 * @param {object|null|undefined} features `user.features`.
 * @param {string} label The child menu item's `label`.
 * @returns {boolean}
 */
export const isSidebarChildLocked = (features, label) => {
  const keys = SIDEBAR_CHILD_FEATURE_KEYS[label];
  return !!keys && keys.some((key) => isFeatureLocked(features, key));
};

/**
 * `mailto:` for Contact Sales, subject "Upgrade request — <Feature> — <Company>" (Story AC11).
 *
 * @param {string} supportEmail
 * @param {string} featureKey
 * @param {string|null|undefined} companyName
 * @returns {string}
 */
export const upgradeRequestMailto = (supportEmail, featureKey, companyName) => {
  const parts = ['Upgrade request', FEATURE_LABELS[featureKey] || featureKey];
  if (companyName) parts.push(companyName);
  return `mailto:${supportEmail}?subject=${encodeURIComponent(parts.join(' — '))}`;
};
